import {MultiMediaUploadSource} from '@Libraries/user-media-library';
import type {Component, ComponentModelDefinition, Editor, ToolbarButtonProps} from 'grapesjs';
import type {ImageData} from '@Libraries/add-media-library';
import type {MediaTypeModalProps} from '@Modals/media-type-modal/media-type-modal';
import {openMediaTypeModal} from '@Modals/media-type-modal/media-type-modal';
import {MediaType} from '@Panels/media-type-panel/media-type-panel';
import {GA4EventName} from '@Libraries/ga-events';
import {UserImageSource} from '@Libraries/user-image-library';
import type {ImportComponentsOptions} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {
  EMAIL_MAKER_COMMANDS,
  EMAIL_MAKER_COMPONENT_TYPES,
  MOBILE_CSS_PROP_PREFIX,
  TABLET_CSS_PROP_PREFIX,
  UNSUPPORTED_EMAIL_STYLES,
} from '@EmailMakerReact/email-maker-react.constants';
import {EmailMakerCanvasDevice} from '@EmailMakerReact/email-maker-react.types';
import {
  EMAIL_EXPLORE_MEDIA_STOCK_TABS,
  isDesktopEmailEditorViewport,
  openAddImageOptionSelector,
  openModalAfterTouchSettles,
} from '@EmailMakerReact/lib/email-maker-media-modals-library';
import type {ComponentRegistrationContext} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/helpers/component-helpers';
import {registerMediaTypes} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/components/register-media-types';
import {registerTextTypes} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/components/register-text-types';
import {registerSocialTypes} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/components/register-social-types';
import {registerTableTypes} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/components/register-table-types';
import {registerMiscTypes} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/components/register-misc-types';
import {registerContainerTypes} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/components/register-container-types';
import {isImageFileDragActive} from '@EmailMakerReact/lib/email-maker-image-drag-drop-library';

// GrapesJS types `unstylable` as a mutable array; share one mutable copy of the readonly constant.
const UNSTYLABLE_EMAIL_STYLES: string[] = [...UNSUPPORTED_EMAIL_STYLES];

const lastTouchEndEventTimeStamps = new WeakMap<EventTarget, number>();

const DOUBLE_TAP_THRESHOLD_MS = 400;

/** Touch devices don't emit native `dblclick`, so two touchend events on one target within the threshold dispatch a synthetic `dblclick`. */
function dispatchDblClickOnDoubleTap(e: TouchEvent): void {
  if (!e.target) {
    return;
  }

  const lastTouchEndEventTimeStamp = lastTouchEndEventTimeStamps.get(e.target);
  if (lastTouchEndEventTimeStamp !== undefined && e.timeStamp - lastTouchEndEventTimeStamp < DOUBLE_TAP_THRESHOLD_MS) {
    const event = new MouseEvent('dblclick');
    e.target.dispatchEvent(event);
  }

  lastTouchEndEventTimeStamps.set(e.target, e.timeStamp);
}

/**
 * MUST run after initCommands: PMW_BUTTON `defaults` eagerly runs GET_TABLE_COMPONENT_FOR_BUTTON
 * during registration, and an unregistered command returns undefined, breaking the button's default children.
 */
export function importComponents(editor: Editor, options: ImportComponentsOptions): void {
  const {isEditorAiContentGenerationEnabled, isAiEditEnabled} = options;

  const toolbarContainerBtn: Partial<ToolbarButtonProps> = {
    attributes: {
      class: 'icon-select-parent size-icon-20 has-tooltip',
      title: window.i18next.t('pmwjs_select_container'),
      rel: 'tooltip',
    },
    label: '',
    events: {
      click: (e: Event): void => {
        e.preventDefault();
        e.stopPropagation();
        editor.runCommand('core:component-exit', {force: 1});
      },
      touchstart: (e: Event): void => {
        e.preventDefault();
        e.stopPropagation();
        editor.runCommand('core:component-exit', {force: 1});
      },
    },
  };

  const toolbarMoveBtn: Partial<ToolbarButtonProps> = {
    attributes: {
      draggable: true,
      class: 'btn-tlb-move size-icon-20 icon-move gjs-no-touch-actions has-tooltip',
      title: window.i18next.t('pmwjs_move'),
      rel: 'tooltip',
    },
    command: 'tlb-move',
  };

  const toolbarMergeTagsBtn: Partial<ToolbarButtonProps> = {
    attributes: {
      class: 'merge-tag-anchor gjs-no-touch-actions has-tooltip',
      title: window.i18next.t('pmwjs_add_merge_tag'),
      rel: 'tooltip',
    },
    events: {
      click: (e: Event): void => {
        e.preventDefault();
        e.stopPropagation();
      },
      touchstart: (e: Event): void => {
        e.preventDefault();
        e.stopPropagation();
      },
    },
  };

  const toolbarAiRewriteBtn: Partial<ToolbarButtonProps> = {
    attributes: {
      class: 'gjs-no-touch-actions',
      title: window.i18next.t('pmwjs_rewrite_with_ai'),
      rel: 'tooltip',
      id: TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR,
    },
    events: {
      click: (e: Event): void => {
        e.preventDefault();
        e.stopPropagation();
      },
      touchstart: (e: Event): void => {
        e.stopPropagation();
      },
    },
  };

  const DEFAULT_TOOLBAR_OPTIONS: Partial<ToolbarButtonProps>[] = [toolbarContainerBtn, toolbarMoveBtn];

  const getImageComponentForElement = (element: HTMLElement): Component | undefined => {
    const allImageComponents = editor.runCommand(EMAIL_MAKER_COMMANDS.GET_ALL_COMPONENTS_BY_TYPE, {types: [EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE]}) as Component[];
    return allImageComponents.find((imageComponent) => imageComponent.getId() === element.id);
  };

  const mediaHoverEventHandlers = {
    mouseenter: (event: MouseEvent): void => {
      const element = getMouseTargetedHTML(event);
      if (!element || isImageFileDragActive(editor)) {
        return;
      }

      if (isAiEditEnabled() && getImageComponentForElement(element)) {
        return;
      }

      editor.runCommand(EMAIL_MAKER_COMMANDS.SHOW_MEDIA_COMPONENT_OVERLAY, {element});
    },
    mouseleave: (event: MouseEvent): void => {
      const element = getMouseTargetedHTML(event);
      if (!element) {
        return;
      }

      editor.runCommand(EMAIL_MAKER_COMMANDS.HIDE_MEDIA_COMPONENT_OVERLAY, {element});
    },
  };

  const mediaClickEventHandler = (event: MouseEvent): void => {
    const element = getMouseTargetedHTML(event);
    if (!element) {
      return;
    }

    const component = getImageComponentForElement(element);
    if (!component) {
      return;
    }

    editor.runCommand(EMAIL_MAKER_COMMANDS.HIDE_MEDIA_COMPONENT_OVERLAY, {element});

    if (isAiEditEnabled()) {
      editor.runCommand(EMAIL_MAKER_COMMANDS.mediaModals.OPEN_AI_IMAGES, {component});
    }
  };

  const getDraggableForColumn = (): string => {
    return `[data-pmw-type="${EMAIL_MAKER_COMPONENT_TYPES.table.PMW_SECTION}"] [data-pmw-type="${EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW}"]`;
  };

  const isDroppableInRow = (target: Component, destination: Component): boolean => {
    return (target.get('type') === EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL &&
      destination.get('type') === EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW &&
      editor.runCommand(EMAIL_MAKER_COMMANDS.IS_COMPONENT_INSIDE_COMPONENT, {child: target, parent: destination})) as boolean;
  };

  const openTouchFriendlyMediaTypeModal = (props: MediaTypeModalProps): void => {
    openModalAfterTouchSettles((): void => {
      openMediaTypeModal(props);
    });
  };

  const stockMediaModelSelectionHandler = (imgs: ImageData[], component: Component): void => {
    if (imgs[0].source === UserImageSource.EMAIL_UPLOAD || imgs[0].source === UserImageSource.UPLOAD) {
      editor.runCommand(EMAIL_MAKER_COMMANDS.TRACK_GA_EVENT_COMPONENT, {
        component: component,
        eventAction: GA4EventName.EMAILMAKER_UPLOAD_MEDIA,
      });
    }

    editor.runCommand(EMAIL_MAKER_COMMANDS.SET_IMAGE_SRC, {
      component: component,
      source: imgs[0].source,
      extension: imgs[0].extension,
      hashedFilename: imgs[0].hashedFilename,
    });
  };

  const isMainWrapperResized = (selected?: Component): boolean => {
    const mainWrapper: Component = editor.runCommand(EMAIL_MAKER_COMMANDS.GET_MAIN_WRAPPER) as Component;
    return selected === mainWrapper;
  };

  const getContainingBlockWidthPx = (element: HTMLElement | null | undefined): number => {
    if (!element) {
      return 0;
    }
    const isAbsolutelyPositioned = getComputedStyle(element).position === 'absolute';
    const containingBlock = isAbsolutelyPositioned ? (element.offsetParent as HTMLElement | null) : element.parentElement;
    return containingBlock?.offsetWidth ?? 0;
  };

  const convertResizerWidthToConfigUnit = (resizerWidthPx: number, unitWidth: string, selected: Component | undefined): number => {
    if (unitWidth !== '%') {
      return resizerWidthPx;
    }
    const containingBlockWidth = getContainingBlockWidthPx(selected?.getEl());
    if (containingBlockWidth <= 0) {
      return resizerWidthPx;
    }
    return Number(((resizerWidthPx / containingBlockWidth) * 100).toFixed(2));
  };

  const mediaResizeOpts = {
    ratioDefault: true,
    step: 1,
    minDim: 10,
    unitWidth: '%',
    keepAutoHeight: true,
    keyWidth: 'width',
    currentUnit: true,
    tl: 1,
    tc: 0,
    tr: 1,
    cl: 0,
    cr: 0,
    bl: 1,
    bc: 0,
    br: 1,
    onEnd: (_ev: Event, opts: {config: {unitWidth: string; keyWidth: string}; resizer: {rectDim: {w: string; h: string}}}): void => {
      const {config} = opts;
      const rect = opts.resizer.rectDim;
      const selected = editor.getSelected();
      const style = selected?.getStyle() ?? {};
      const activeDeviceId = editor.Devices.getSelected()?.id;
      const isTablet = activeDeviceId === EmailMakerCanvasDevice.TABLET;
      const isMobile = activeDeviceId === EmailMakerCanvasDevice.MOBILE;

      if (isMainWrapperResized(selected)) {
        return;
      }

      let cssPrefix = '';

      const {unitWidth} = config;
      let {keyWidth} = config;

      const widthNumeric = convertResizerWidthToConfigUnit(parseFloat(rect.w), unitWidth, selected);
      const widthValue = `${widthNumeric}${unitWidth}`;

      if (isTablet) {
        cssPrefix = TABLET_CSS_PROP_PREFIX;
      } else if (isMobile) {
        cssPrefix = MOBILE_CSS_PROP_PREFIX;
      }

      if (isTablet || isMobile) {
        keyWidth = cssPrefix + keyWidth;
        editor.runCommand(EMAIL_MAKER_COMMANDS.SET_DEVICE_MEDIA_QUERY, {
          id: selected?.ccid,
          property: keyWidth,
          value: widthValue,
          deviceID: activeDeviceId,
          cssPrefix,
        });
      }

      style[keyWidth] = widthValue;
      selected?.setStyle(style);
      editor.runCommand(EMAIL_MAKER_COMMANDS.UPDATE_SLIDER_VALUE, {property: keyWidth, value: widthNumeric});
    },
  };

  const openChangeImageModal = (): void => {
    const component = editor.getSelected();

    if (!component) {
      return;
    }

    if (isDesktopEmailEditorViewport()) {
      openAddImageOptionSelector(editor, component);
      return;
    }

    openTouchFriendlyMediaTypeModal({
      mediaType: MediaType.IMAGE,
      title: window.i18next.t('pmwjs_select_an_option'),
      onStartAddSelected: () => {
        editor.runCommand(EMAIL_MAKER_COMMANDS.SET_IMAGE_LOADING, {
          component: component,
          loading: true,
          text: window.i18next.t('pmwjs_loading'),
        });
      },
      onAddSelected: (imgs: ImageData[]) => {
        stockMediaModelSelectionHandler(imgs, component);
      },
      hideRecordingOptions: true,
      uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
      maxSelectionNumber: 1,
      exploreMediaStockTabs: EMAIL_EXPLORE_MEDIA_STOCK_TABS,
      myDesignSelectedCallback: () => {
        editor.runCommand(EMAIL_MAKER_COMMANDS.OPEN_DESIGN_SELECTOR, {isMultiSelectAllowed: false, replaceComponent: true});
      },
      hideFooterSecondaryActions: true,
    });
  };

  function onTextTouchEnd(this: ComponentModelDefinition, e: TouchEvent): void {
    if (this.model === editor.getEditing()) {
      e.stopPropagation();
      return;
    }

    dispatchDblClickOnDoubleTap(e);
  }

  function getMouseTargetedHTML(event: MouseEvent): HTMLElement | null {
    const target = event.currentTarget;

    if (!target || !(target instanceof HTMLElement)) {
      return null;
    }

    return target;
  }

  const initRemovalOfUnsupportedCssProperties = (component: Component): void => {
    editor.runCommand(EMAIL_MAKER_COMMANDS.REMOVE_UNSUPPORTED_STYLES, {
      component: component,
    });
  };

  /** Returns a fresh toolbar array for text-like components (merge-tags + container + move, with AI rewrite prepended when enabled). */
  function buildTextToolbar(): Partial<ToolbarButtonProps>[] {
    const toolbar: Partial<ToolbarButtonProps>[] = [toolbarMergeTagsBtn, ...DEFAULT_TOOLBAR_OPTIONS];
    if (isEditorAiContentGenerationEnabled) {
      toolbar.unshift(toolbarAiRewriteBtn);
    }
    return toolbar;
  }

  const buttonToolbarOptions = buildTextToolbar();
  const textToolbarOptions = buildTextToolbar();

  const ctx: ComponentRegistrationContext = {
    editor,
    options,
    toolbarContainerBtn,
    toolbarMoveBtn,
    toolbarMergeTagsBtn,
    toolbarAiRewriteBtn,
    defaultToolbarOptions: DEFAULT_TOOLBAR_OPTIONS,
    unstylableEmailStyles: UNSTYLABLE_EMAIL_STYLES,
    dispatchDblClickOnDoubleTap,
    mediaHoverEventHandlers,
    mediaClickEventHandler,
    openChangeImageModal,
    mediaResizeOpts,
    isDroppableInRow,
    getDraggableForColumn,
    initRemovalOfUnsupportedCssProperties,
    onTextTouchEnd,
    buttonToolbarOptions,
    textToolbarOptions,
  };

  registerMediaTypes(ctx);
  registerTextTypes(ctx);
  registerSocialTypes(ctx);
  registerTableTypes(ctx);
  registerMiscTypes(ctx);
  // Must register last: PMW_READ_ONLY_FOOTER's isComponent matcher overlaps default.TEXT / PMW_DIV; GrapesJS resolves last-wins.
  registerContainerTypes(ctx);
}
